<template>
    <div>
        <headline-container headline="Subscriptions" :breadcrumbs="[{text: 'Subscriptions'}]"/>

        <div class="container">
            <div class="row">
                <div class="col-sm-8">
                    <div style="margin-bottom: 10px;">
                        <label>
                            User
                        </label>

                        <select-widget
                            v-model="user"
                            :options="userOptions"
                            :nullable="true"
                            placeholder="(all users)"
                        ></select-widget>
                    </div>

                    <div>
                        <label>
                            Metric
                        </label>

                        <select-widget
                            v-model="metricType"
                            :options="[{value: 'revenue', label: 'Revenue'}, {value: 'count', label: 'Count'}]"
                        ></select-widget>
                    </div>
                </div>

                <div class="col-sm-8">
                    <div style="margin-bottom: 10px;">
                        <label>
                            Subscriber Type
                        </label>

                        <select-widget
                            v-model="subscriberType"
                            :options="[{value: 'companies', label: 'Companies'}, {value: 'people', label: 'People'}]"
                            :nullable="true"
                            placeholder="(all subscriber types)"
                        ></select-widget>
                    </div>

                    <div>
                        <label>
                            Year
                        </label>

                        <div class="dashboard-date-widget">
                            <date-widget v-model="year" format="YYYY" original-format="YYYY"></date-widget>

                            <i class="dashboard-date-widget__icon fa fa-sort"></i>
                        </div>
                    </div>
                </div>

                <div class="col-sm-8">
                    <label>
                        Portal
                    </label>

                    <multi-select-widget
                        v-model="portal"
                        :options="portalOptions"
                        :nullable="true"
                        placeholder="(all portals)"
                    ></multi-select-widget>
                </div>
            </div>

            <hr>

            <div class="row">
                <div class="col-sm-6">
                    <net-change-number
                        :year="year"
                        :metric-type="metricType"
                        :portal="portal"
                        :user="user"
                        :subscriber-type="subscriberType"
                    ></net-change-number>
                </div>

                <div class="col-sm-6">
                    <new-number
                        :year="year"
                        :metric-type="metricType"
                        :portal="portal"
                        :user="user"
                        :subscriber-type="subscriberType"
                    ></new-number>
                </div>

                <div class="col-sm-6">
                    <renewed-number
                        :year="year"
                        :metric-type="metricType"
                        :portal="portal"
                        :user="user"
                        :subscriber-type="subscriberType"
                    ></renewed-number>
                </div>

                <div class="col-sm-6">
                    <average-price
                        :year="year"
                        :portal="portal"
                        :user="user"
                        :subscriber-type="subscriberType"
                    ></average-price>
                </div>
            </div>

            <div class="row">
                <div class="col-sm-12">
                    <renewal-percentage
                        :year="year"
                        :metric-type="metricType"
                        :portal="portal"
                        :user="user"
                        :subscriber-type="subscriberType"
                    ></renewal-percentage>
                </div>

                <div class="col-sm-12">
                    <subscription-count
                        :year="year"
                        :metric-type="metricType"
                        :portal="portal"
                        :user="user"
                        :subscriber-type="subscriberType"
                    ></subscription-count>
                </div>
            </div>

            <div class="row">
                <div class="col-sm-24">
                    <sales-development
                        :year="year"
                        :metric-type="metricType"
                        :portal="portal"
                        :user="user"
                        :subscriber-type="subscriberType"
                    ></sales-development>
                </div>
            </div>

            <div class="row">
                <div class="col-sm-24">
                    <change-development
                        :year="year"
                        :metric-type="metricType"
                        :portal="portal"
                        :user="user"
                        :subscriber-type="subscriberType"
                    ></change-development>
                </div>
            </div>

            <div v-if="hasPermission('admin')" class="row">
                <div class="col-sm-24">
                    <accrued-value-panel
                        :year="year"
                        :portal="portal"
                        :user="user"
                        :subscriber-type="subscriberType"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<style>
.dashboard-date-widget {
    position: relative;
}

.dashboard-date-widget__icon {
    position: absolute;
    top: 50%;
    right: 10px;
    transform: translateY(-50%);
}
</style>

<script>
import http from '@/http'
import moment from 'moment'
import AccruedValuePanel from '@/components/Dashboards/Subscriptions/AccruedValuePanel'
import AveragePrice from '@/pages/Dashboards/Subscriptions/Panels/AveragePrice'
import ChangeDevelopment from '@/pages/Dashboards/Subscriptions/Panels/ChangeDevelopment'
import DateWidget from '@/vendor/forms/FormGroups/Widgets/Date'
import HeadlineContainer from '@/components/Layout/HeadlineContainer'
import MultiSelectWidget from '@/components/FormGroups/Widgets/MultiSelect'
import NetChangeNumber from '@/pages/Dashboards/Subscriptions/Panels/NetChangeNumber'
import NewNumber from '@/pages/Dashboards/Subscriptions/Panels/NewNumber'
import Permissions from '@/mixins/Permissions'
import RenewalPercentage from '@/pages/Dashboards/Subscriptions/Panels/RenewalPercentage'
import RenewedNumber from '@/pages/Dashboards/Subscriptions/Panels/RenewedNumber'
import SalesDevelopment from '@/pages/Dashboards/Subscriptions/Panels/SalesDevelopment'
import SelectWidget from '@/vendor/forms/FormGroups/Widgets/Select'
import SubscriptionCount from '@/pages/Dashboards/Subscriptions/Panels/SubscriptionCount'

export default {
    components: {
        AccruedValuePanel,
        AveragePrice,
        DateWidget,
        HeadlineContainer,
        ChangeDevelopment,
        MultiSelectWidget,
        NetChangeNumber,
        NewNumber,
        RenewalPercentage,
        RenewedNumber,
        SalesDevelopment,
        SelectWidget,
        SubscriptionCount
    },

    mixins: [Permissions],

    data() {
        return {
            year: moment().format('YYYY'),
            metricType: 'revenue',
            portal: null,
            user: null,
            subscriberType: 'companies',

            portals: null,
            users: null,

            loading: true
        }
    },

    mounted() {
        this.load()
    },

    computed: {
        portalOptions() {
            if (this.loading) {
                return []
            }

            return this.portals.map(portal => {
                return {
                    value: portal.id,
                    label: portal.attributes.name
                }
            })
        },

        userOptions() {
            if (this.loading) {
                return []
            }

            return this.users.map(user => {
                return {
                    value: user.id,
                    label: user.attributes.full_name
                }
            })
        },

        lastSixMonthsStartDate() {
            return moment().subtract(6, 'months').format('YYYY-MM-DD')
        },

        lastSixMonthsEndDate() {
            return moment().format('YYYY-MM-DD')
        },

        lastYearStartDate() {
            return moment().subtract(1, 'year').startOf('year').format('YYYY-MM-DD')
        },

        lastYearEndDate() {
            return moment().subtract(1, 'year').endOf('year').format('YYYY-MM-DD')
        },

        lastYearDescription() {
            return moment().subtract(1, 'year').format('YYYY')
        },

        previousYearStartDate() {
            return moment().subtract(2, 'years').startOf('year').format('YYYY-MM-DD')
        },

        previousYearEndDate() {
            return moment().subtract(2, 'years').endOf('year').format('YYYY-MM-DD')
        },

        previousYearDescription() {
            return moment().subtract(2, 'years').format('YYYY')
        }
    },

    methods: {
        load() {
            this.loading = true

            Promise.all([
                http.get('/api/v2/portals'),
                http.get('/api/v2/users')
            ]).then(responses => {
                this.portals = responses[0].data.data

                this.users = responses[1].data.data

                this.loading = false
            })
        }
    }
}
</script>
